import * as React from 'react';
import {
  keyframes,
  IStyle,
  IStyleFunctionOrObject,
  ITheme,
  styled,
  concatStyleSetsWithProps,
  mergeStyles
} from '@fluentui/react';
import { IFluidObject } from '@fluidframework/core-interfaces';
import { ExternalContainer, LayoutMode, TokenProvider, SettingsProvider } from '@fluidx/office-fluid-types';
import { Locale, LocaleDirection } from '@fluidx/utilities';
import { ComponentUXContextProvider } from './ComponentUXContextProvider';
import { getStringTable } from '../../utilities/localizationUtilities';
import { get_RGBA_or_RRGGBBAA_ColorFromThemeSlot } from '../../utilities/colorParser';
import { HostUxStrings } from '../../stringTypes/HostUxStrings';

export const componentUXWrapperTestId = 'componentUXWrapperTestId';

/** Type of the callback for triggering the activity animation. */
export type TriggerAnimation = () => void;

/** Configuration for how the Component Ux should look/behave */
export interface ComponentUXWrapperProps {
  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };

  /**
   * Layout mode of render target which defines which dimensions are controlled
   * by the component and which dimensions are controlled by the host. Omitting
   * this value assumes that the host will style the outer-most element of the
   * ux to respect the layout behavior the component will be given.
   */
  layoutMode?: LayoutMode;

  /** Set true to hide ComponentUXWrapper */
  hidden?: boolean;

  /** Loaded component */
  component?: IFluidObject;

  /** Loaded container */
  container?: ExternalContainer;

  /**
   * Host token provider.
   * This will be available to all the composed Component UX parts that need to get tokens or determine signed user identity type
   */
  tokenProvider?: TokenProvider;

  /**
   * Settings provider
   * This will be available to all the composed Component UX parts that wants to read settings from a settingsProvider
   */
  settingsProvider?: SettingsProvider;

  /**
   * Callback for when the wrapper knows to build a function to trigger the activity animation.
   * @param triggerAnimation the function which will trigger the animation
   */
  setTriggerAnimation?: (triggerAnimation: TriggerAnimation) => void;

  /**
   * Whether to show the activity animation on the first load.
   * By default the animation is shown on first render.
   * */
  noInitialAnimation?: boolean;

  /** Elements to render as Chrome and ComponentSpace experiences of Component UX */
  children?: React.ReactNode;

  /** Custom styling to be applied to the outer element */
  styles?: IStyleFunctionOrObject<ComponentUXWrapperStyleProps, ComponentUXWrapperStyles>;

  /**
   * Additional property to synchronize the border width on the root element before, during and after signature animation.
   * This prevents jarring border resize during these steps of component load.
   * Use this property especially if you are customizing the border dimensions via root IStyles
   */
  standardRootBorderWidthPx?: number;

  /** Theme used in componentUX */
  theme?: ITheme;

  /** Reference to the componentUX wrapper. This is used to allow host to set focus on the wrapper element */
  wrapperRef?: React.RefObject<HTMLDivElement>;
}

interface ComponentUXWrapperStyleProps
  extends Pick<ComponentUXWrapperProps, 'theme' | 'layoutMode' | 'hidden' | 'standardRootBorderWidthPx'> {
  animationVisible: boolean;
}

export interface ComponentUXWrapperStyles {
  root?: IStyle;

  /** How to hide the Component UX. Applied to the root element after the root style if the UX is hidden. */
  hidden?: IStyle;
}

/**
 * Wraps the whole Component UX experience together under a single element.
 *
 * This element has a number of responsibilities which should be considered if a
 * custom 'wrapper' is used:
 * - The border activity animation
 * - Layout styling to integrate with host DOM/styling
 * - Focus navigation via keyboard (entire UX is a single tab stop which can be 'entered')
 * - Hiding the UX while allowing it to be attached when loading in an iframe
 */
export const ComponentUXWrapper = React.memo(
  styled((props: ComponentUXWrapperProps) => {
    const {
      children,
      localization,
      setTriggerAnimation,
      noInitialAnimation,
      component,
      container,
      tokenProvider,
      settingsProvider,
      wrapperRef,
      styles
    } = props;

    // TODO: review for violation of rules of hooks (hooks must be called in React function component or custom hook)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [animationVisible, setAnimationVisible] = React.useState<boolean>(!noInitialAnimation);
    // TODO: review for violation of rules of hooks (hooks must be called in React function component or custom hook)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [stringTable, setStringTable] = React.useState<HostUxStrings>();

    // TODO: review for violation of rules of hooks (hooks must be called in React function component or custom hook)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const componentUXWrapperElement = wrapperRef || React.useRef<HTMLDivElement>(null);

    const triggerAnimation = (): void => {
      setAnimationVisible(true);
    };

    const clearAnimation = (): void => {
      setAnimationVisible(false);
    };

    // TODO: review for violation of rules of hooks (hooks must be called in React function component or custom hook)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      if (setTriggerAnimation) {
        setTriggerAnimation(triggerAnimation);
      }
    }, [setTriggerAnimation]);

    // TODO: review for violation of rules of hooks (hooks must be called in React function component or custom hook)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      let cancelImport = false;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getStringTable(localization.locale).then((table) => {
        !cancelImport && setStringTable(table);
      });

      return () => {
        cancelImport = true;
      };
      // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resolvedStyles = concatStyleSetsWithProps({ ...props, animationVisible }, styles);
    const wrapperClassName = mergeStyles(resolvedStyles.root, resolvedStyles.hidden);

    return stringTable ? (
      <div
        className={wrapperClassName}
        ref={componentUXWrapperElement}
        aria-label={
          component ? stringTable.componentUXWrapperAriaLabel : stringTable.componentUXWrapperLoadingAriaLabel
        }
        data-testid={componentUXWrapperTestId}
        // eslint-disable-next-line react/jsx-no-bind
        onAnimationEnd={clearAnimation}
      >
        <ComponentUXContextProvider
          container={container}
          component={component}
          tokenProvider={tokenProvider}
          stringTable={stringTable}
          localization={localization}
          settingsProvider={settingsProvider}
        >
          {children}
        </ComponentUXContextProvider>
      </div>
    ) : null;
  }, getDefaultStyle)
);

/**
 * Gets a default set of style properties based on the given layout mode.  For
 * dimensions given by the host, it uses height/width=100%.  For dimensions
 * given by the component, it uses the the behavior of 'block' and
 * 'inline-block' display modes.
 *
 * An alternate approach to compositing the 'growHeight' mode would be to make
 * an ancestor element 'display=flex' and supply the property 'flexgrow=1'.
 * @param layoutMode
 */
function getCompositionStyleFromLayoutMode(layoutMode: LayoutMode): IStyle {
  return {
    display: layoutMode === LayoutMode.grow ? 'inline-flex' : undefined,
    width: layoutMode !== LayoutMode.grow ? '100%' : undefined,
    height: layoutMode === LayoutMode.fixedByHost ? '100%' : undefined,
    maxWidth: '100%',
    position: 'relative'
  };
}

/**
 * Generates style definition for the multitude of different responsibilities
 * the wrapper manages
 */
function getDefaultStyle(props: ComponentUXWrapperStyleProps): ComponentUXWrapperStyles {
  const {
    theme,
    layoutMode,
    hidden,
    animationVisible,
    standardRootBorderWidthPx = 2 /* default border width is 2px */
  } = props;

  // Styling related to this element being a parent to the rest of the UX
  const baseStyle: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    background: theme ? theme.palette.neutralLighterAlt : '#FAFAFA',
    position: 'relative'
  };

  // If a layout mode is specified, apply the default style implementation for the given mode
  const layoutStyle: IStyle | undefined =
    layoutMode !== undefined ? getCompositionStyleFromLayoutMode(layoutMode) : undefined;

  // Styling related to having a border in its non-animated state
  const borderStyle: IStyle = {
    boxSizing: 'border-box',
    border: `${standardRootBorderWidthPx}px solid transparent`,
    borderRadius: `${standardRootBorderWidthPx * 2}px`
  };

  // Style for actually playing the activity animation
  const animationStyle: IStyle | undefined = animationVisible
    ? getSignatureAnimation(standardRootBorderWidthPx, theme)
    : undefined;

  // Style to hide the entire UX. Used to not show any UX while still needing to have the DOM in place to load iframed components.
  const hiddenStyle: IStyle | undefined = hidden ? getHiddenStyles(layoutMode) : undefined;

  return {
    root: [baseStyle, layoutStyle, borderStyle, animationStyle],
    hidden: hiddenStyle
  };
}

/**
 * We use absolute positioning and 0 dimensional bounds to hide the Component UX
 * 'display:none' is not used to prevent the case where an inner
 * component may be using 'requestAnimationFrame' during render.
 * Iframes in 'display:none' DOM may not generate animation frames.
 *
 * @param layoutMode LayoutMode of the outer hosting element.
 * Component UX inner hosting element should have this LayoutMode even after applying hidden styles
 */
const getHiddenStyles = (layoutMode?: LayoutMode): IStyle | undefined => {
  const getDimensions = () => {
    switch (layoutMode) {
      case LayoutMode.fixedByHost:
      case LayoutMode.growHeight:
        return { minHeight: 0, maxHeight: 0 };
      case LayoutMode.grow:
        return {
          minHeight: 0,
          maxHeight: 0,
          minWidth: 0,
          maxWidth: 0
        };
      default:
        return {};
    }
  };

  return {
    position: 'absolute',
    borderWidth: 0,
    overflow: 'hidden',
    animation: 'none',
    ...getDimensions()
  };
};

/**
 * Generates a themed signature animation.
 * Note: The color values passed must be RGB or HEX values (RRGGBB).
 * Colors provided as CSS variables will not work, and animation will assume default color values
 * @param borderWidthPx Standard border width in pixels
 * @param theme Theme used in ComponentUX
 */
function getSignatureAnimation(borderWidthPx: number, theme: ITheme | undefined): IStyle {
  // Animated border
  const defaultAnimationBorderColor = '#0078d4';
  const defaultAnimationShadowColor = '#EDEBE9';

  return {
    animation: `${keyframes({
      '0%': {
        border: `${borderWidthPx}px solid ${get_RGBA_or_RRGGBBAA_ColorFromThemeSlot(
          defaultAnimationBorderColor,
          theme?.palette.accent,
          0
        )}`,
        boxShadow: '0px 1.2px 3.6px 0px transparent, 0px 6.4px 14.4px 0px transparent'
      },
      '10%': {
        border: `${borderWidthPx}px solid ${get_RGBA_or_RRGGBBAA_ColorFromThemeSlot(
          defaultAnimationBorderColor,
          theme?.palette.accent,
          1
        )}`,
        boxShadow: `0px 1.2px 3.6px 0px , 0px 6.4px 14.4px 0px ${get_RGBA_or_RRGGBBAA_ColorFromThemeSlot(
          defaultAnimationShadowColor,
          theme?.palette.neutralLight
        )}`
      },
      '100%': {
        border: `${borderWidthPx}px solid ${get_RGBA_or_RRGGBBAA_ColorFromThemeSlot(
          defaultAnimationBorderColor,
          theme?.palette.accent,
          0
        )}`,
        boxShadow: '0px 1.2px 3.6px 0px transparent, 0px 6.4px 14.4px 0px transparent'
      }
    })} 2s linear`,
    // Animate outline in high contrast mode
    '@media (forced-colors: active), (prefers-contrast: more), (-ms-high-contrast: active)': {
      animation: `${keyframes({
        '0%': {
          outline: `none`
        },
        '10%': {
          outline: `2px solid transparent`
        },
        '100%': {
          outline: `none`
        }
      })} 2s linear`
    },
    // Respect reduced motion preference
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none'
    }
  };
}

// cspell:ignore iframes iframed testid tabbable flexgrow RRGGBB RRGGBBAA
